import React, { useState, useEffect, useCallback } from 'react';
import Table from '../../../components/Table/Table';
import SearchBar from '../../../components/SearchBar/SearchBar';
import Button from '../../../components/Button/Button';
import Dropdown from '../../../components/Dropdown/Dropdown';
import Pagination from '../../../components/Pagination/Pagination';
import { useAdminUsers } from '../../../hooks/useAdminUsers';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import type { AdminUserDto } from '../../../services/api';
import AccountStatusConfirmModal from '../AccountStatus/AccountStatusConfirmModal';
import styles from './UsersPage.module.css';

const ROLE_OPTIONS = ['All Roles', 'SalonOwner', 'SalonStaff', 'SuperAdmin'];

const UsersPage: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebouncedValue(searchTerm, 300);
    const [roleFilter, setRoleFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);

    const { loading, error, users, totalCount, fetchUsers, toggleStatus } = useAdminUsers();
    const { t } = useLanguage();
    // AB#2112 — the account whose state the admin is about to change (confirmed first).
    const [statusTarget, setStatusTarget] = useState<{ user: AdminUserDto; nextActive: boolean } | null>(null);

    const loadUsers = useCallback(async () => {
        try {
            await fetchUsers({
                search: debouncedSearch || undefined,
                role: roleFilter || undefined,
                page: currentPage,
                pageSize
            });
        } catch {
            // handled by hook
        }
    }, [fetchUsers, debouncedSearch, roleFilter, currentPage, pageSize]);

    useEffect(() => { loadUsers(); }, [loadUsers]);

    const handleSearchChange = useCallback((value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    }, []);

    const formatDate = (dateString: string): string => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return '—';
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const tableHeaders = ['Name', 'Email', 'Role', 'Status', 'Created', 'Actions'];
    const tableData = (users ?? []).map((u, i) => {
        const status = u.status ?? '';
        const isActive = status.toLowerCase() === 'active';
        return {
            originalIndex: i,
            'Name': u.fullName,
            'Email': u.email,
            'Role': u.role,
            'Status': (
                <span className={`${styles.statusBadge} ${isActive ? styles.statusActive : styles.statusInactive}`}>
                    {status || '—'}
                </span>
            ),
            'Created': formatDate(u.lastLogin),
            'Actions': (
                <Button
                    label={t(isActive ? 'superadmin.accountStatus.action.deactivate' : 'superadmin.accountStatus.action.activate')}
                    onClick={() => setStatusTarget({ user: u, nextActive: !isActive })}
                    variant="secondary"
                    size="small"
                />
            )
        };
    });

    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>Users</h1>
                <p className={styles.subtitle}>Manage all platform users</p>
            </div>

            <div className={styles.controlsHeaderContainer}>
                <div className={styles.searchContainer}>
                    <SearchBar
                        placeholder={t('superadmin.users.searchPlaceholder')}
                        onChange={handleSearchChange}
                        value={searchTerm}
                        showClearButton={true}
                    />
                </div>
                <div className={styles.filterContainer}>
                    <Dropdown
                        options={ROLE_OPTIONS}
                        value={roleFilter || 'All Roles'}
                        onChange={(val) => {
                            const v = val === 'All Roles' ? '' : String(val);
                            setRoleFilter(v);
                            setCurrentPage(1);
                        }}
                        defaultMessage="Filter by role"
                    />
                </div>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    {error}
                    <Button label="Retry" onClick={loadUsers} size="small" />
                </div>
            )}

            <div className={styles.tableWrapper}>
                <Table
                    headers={tableHeaders}
                    data={tableData}
                    loading={loading}
                    nonSortableColumns={['Actions', 'Status']}
                    customRenderers={{ 'Status': (v) => v, 'Actions': (v) => v }}
                />
            </div>

            {!loading && totalPages > 1 && (
                <div className={styles.paginationWrapper}>
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                </div>
            )}

            {statusTarget && (
                <AccountStatusConfirmModal
                    open
                    kind="user"
                    target={{ id: statusTarget.user.id, fullName: statusTarget.user.fullName, email: statusTarget.user.email }}
                    nextActive={statusTarget.nextActive}
                    onConfirm={() => toggleStatus(statusTarget.user.id, statusTarget.nextActive)}
                    onClose={() => setStatusTarget(null)}
                    onChanged={loadUsers}
                />
            )}

            {(users?.length ?? 0) === 0 && !loading && !error && (
                <div className={styles.emptyState}>
                    <h3>No users found</h3>
                    <p>Try adjusting your search or filters.</p>
                </div>
            )}
        </div>
    );
};

export default UsersPage;
